// tournamentSession.ts (TF1) - the CONTINUOUS tournament the Local surface plays over. Where
// tournamentRunner.ts deals ONE frozen ICM call-off spot, this runner drives a whole running
// tournament: it holds the engine `FieldState` (the shrinking multi-table field), deals hero a real
// hand each step, wires the SHORT-STACK FIRST-IN decision through PF5's `recommendPreflop` (the DT Nash
// jam-or-fold charts - closing TOURNAMENT-QA GAP-1/GAP-2), resolves the chip result, and advances the
// field so hero's stack / blind level / M / ICM evolve hand-to-hand. Deterministic in the run seed.
//
// HONESTY (North Star §4.1): every number rendered is an engine output. The field dynamics come from
// `tournament/field.ts` (chip-conserving, seeded); the jam-or-fold membership is the DT Nash chart via
// `recommendPreflop` (NOT re-derived here); the ICM strip is priced off the live running stacks; the
// jam OUTCOME is resolved by hero's real all-in equity (TsEquityProvider) vs a call range. The one
// authored teaching datum is the villains' representative short-stack CALL range (below) - it sets the
// equity of a contested jam only, exactly as tournamentRunner authors the bubble jam range.
import {
  advanceField,
  aliveCount,
  createRng,
  currentLevel,
  heroChips,
  heroRank,
  heroStackContext,
  initField,
  isPushFoldDepth,
  livePayouts,
  priceHeroField,
  recommendPreflop,
  parseRange,
  TsEquityProvider,
  createDealer,
  classifyCombo,
  cardToString,
  LOCAL_100_SCHEDULE,
  LOCAL_200_SCHEDULE,
  LOCAL_100_START_CHIPS,
  LOCAL_200_START_CHIPS,
  type FieldState,
  type PreflopContext,
} from '@postflop/engine';
import type { BlindSchedule } from '@postflop/engine';
import type {
  CardIndex,
  PayoutStructure,
  Position,
  PreflopRecommendation,
  TournamentStage,
} from '@postflop/schema';
import { payoutStructureOf } from './tournamentData';
import { loadPushFoldChart } from './pushFoldChart';
import { archetypeOf } from './liveData';
import type { TournamentContextValues, TournamentPresetId } from './tournamentRunner';

const SCHEDULE_FOR: Record<TournamentPresetId, BlindSchedule> = {
  local_100: LOCAL_100_SCHEDULE,
  local_200: LOCAL_200_SCHEDULE,
};
const START_CHIPS_FOR: Record<TournamentPresetId, number> = {
  local_100: LOCAL_100_START_CHIPS,
  local_200: LOCAL_200_START_CHIPS,
};

/** The open positions hero can be first-in from (never BB - the BB never opens first-in). Rotated
 *  deterministically by hand number so hero sees the full range of push/fold open spots. */
const OPEN_POSITIONS: readonly Position[] = ['UTG', 'LJ', 'CO', 'BTN', 'SB'];

/** A representative short-stack CALL range for a villain who wakes up behind hero's jam (~14%): the
 *  tight range a competent short-stack player calls a shove with. Authored teaching datum - it sets the
 *  EQUITY of a contested jam only; the jam/fold decision itself is the DT Nash chart. */
const VILLAIN_CALL_RANGE = '55+, A8s+, ATo+, KTs+, KQo, QJs';

const equityProvider = new TsEquityProvider(createRng('tf1-session-equity'));

/** The full continuous-tournament session state - the FieldState plus the run identity, all plain JSON
 *  (numbers/strings) so `JSON.stringify` round-trips it for save/resume. */
export interface TournamentSessionState {
  runName: string;
  seed: string;
  presetId: TournamentPresetId;
  field: FieldState;
  /** terminal flags: hero busted, or hero won the tournament (heads-up resolved in hero's favor). */
  finished: boolean;
  result: 'running' | 'busted' | 'won';
  /** a short human ticker of the last step (busts + hero's result), for the UI. */
  lastEvent: string;
}

export interface StartTournamentInputs {
  presetId: TournamentPresetId;
  seed: string;
  runName: string;
  /** boot the field already thinned + hero at short-stack depth (a "resume mid-tournament" state), so
   *  the signature first-in jam-or-fold is immediately playable. Deterministic. */
  bootToShort?: boolean;
}

/**
 * Start (or restart) a continuous tournament. A fresh run boots a full field of equal deep stacks at
 * level 1; `bootToShort` pre-advances the field (hero bleeding blinds while the field churns) until hero
 * is at push/fold depth, so the jam-or-fold spot is live from the first hand.
 */
export const startTournament = (inp: StartTournamentInputs): TournamentSessionState => {
  const structure = payoutStructureOf(inp.presetId);
  const schedule = SCHEDULE_FOR[inp.presetId];
  let field = initField({
    structure,
    schedule,
    startingChips: START_CHIPS_FOR[inp.presetId],
    seed: inp.seed,
  });

  if (inp.bootToShort) {
    // Advance until hero is short (<= 14bb) or we run out of levels - hero bleeds ~his blind share each
    // hand while the field thins. Deterministic (advanceField is seeded on field.handNo).
    let guard = 0;
    while (guard++ < 400) {
      const ctx = heroStackContext(field, schedule);
      if (ctx.effectiveStackBB <= 14 || field.level >= schedule.levels.length) break;
      const level = currentLevel(field, schedule);
      const bleed = -Math.round((level.sb + level.bb + level.ante) / 9);
      field = advanceField(field, schedule, { deltaChips: bleed }).state;
    }
  }

  return {
    runName: inp.runName,
    seed: inp.seed,
    presetId: inp.presetId,
    field,
    finished: false,
    result: 'running',
    lastEvent: inp.bootToShort ? 'Sat down short - jam or fold.' : 'Cards in the air.',
  };
};

// ── Dealing a hero spot ─────────────────────────────────────────────────────────────────────────────

/** What the surface renders + grades for one hero hand. */
export interface HeroSpot {
  handNo: number;
  heroCards: readonly [CardIndex, CardIndex];
  heroCardStrings: [string, string];
  heroPosition: Position;
  handLabel: string;
  /** the live context strip - every value an engine output for the CURRENT field. */
  context: TournamentContextValues;
  /** true when hero is at push/fold depth (<= 20bb): the decision is a DT Nash jam-or-fold via PF5. */
  isPushFold: boolean;
  /** THE INSTRUMENTED PROOF: recommendPreflop routed to push/fold (ctx.pushFold was injected AND the
   *  Nash path answered). Read live by the QA sweep - not an assertion from source. */
  pf5Engaged: boolean;
  /** the DT Nash recommendation (present when isPushFold): finalIntent is 'jam' | 'fold'. */
  recommendation?: PreflopRecommendation;
  /** the chart id + band that answered, for the coach line + the proof. */
  chartId?: string;
  band?: string;
}

const HAND_CLASS_LABEL: Record<string, string> = {
  premium_pair: 'a premium pair',
  strong_pair: 'a strong pair',
  medium_pair: 'a middling pair',
  small_pair: 'a small pair',
  ace_high: 'ace high',
  suited_connector: 'a suited connector',
  suited_broadway: 'suited broadways',
  offsuit_broadway: 'offsuit broadways',
  suited_ace: 'a suited ace',
};

const stageForField = (alive: number, paidPlaces: number, fieldSize: number): TournamentStage => {
  if (alive <= 2) return 'headsUp';
  if (alive <= paidPlaces) return alive <= 9 ? 'finalTable' : 'itm';
  if (alive <= paidPlaces + 3) return 'bubble';
  if (alive <= Math.max(paidPlaces + 4, Math.floor(fieldSize / 2))) return 'middle';
  return 'early';
};

const STAGE_LABEL: Record<TournamentStage, string> = {
  early: 'Early',
  middle: 'Middle',
  bubble: 'Bubble',
  itm: 'In the money',
  finalTable: 'Final table',
  headsUp: 'Heads-up',
};

/** Build the live context strip off the current field - the same shape tournamentRunner produces, so the
 *  existing <TournamentContext> renders it unchanged, but every value is a running-field engine output. */
export const buildContext = (
  state: TournamentSessionState,
  structure: PayoutStructure,
  schedule: BlindSchedule,
): TournamentContextValues => {
  const { field } = state;
  const level = currentLevel(field, schedule);
  const sc = heroStackContext(field, schedule);
  const alive = aliveCount(field);
  const icm = priceHeroField(field, structure.payoutFractions, level);
  const stage = stageForField(alive, structure.paidPlaces, structure.fieldSize);

  return {
    presetId: state.presetId,
    buyInLabel: structure.buyInLabel,
    fieldSize: structure.fieldSize,
    paidPlaces: structure.paidPlaces,
    stage,
    stageLabel: STAGE_LABEL[stage],
    levelNumber: level.level,
    sb: level.sb,
    bb: level.bb,
    ante: level.ante,
    heroChips: sc.chips,
    effectiveStackBB: sc.effectiveStackBB,
    m: sc.m,
    mZone: sc.mZone,
    stackStage: sc.stackStage,
    pressure: icm?.pressure ?? 'none',
    riskPremium: icm?.riskPremium ?? 0,
    bubbleFactor: icm?.bubbleFactor ?? 1,
    heroCovers: icm?.heroCovers ?? true,
    playersLeft: alive,
    heroStackRank: heroRank(field),
    payoutsInPlay: livePayouts(structure.payoutFractions, alive),
  };
};

/** Deal hero's next hand: cards, position, the live strip, and (at push/fold depth) the PF5 Nash
 *  jam-or-fold recommendation with the injection proven live. Pure/deterministic in (seed, handNo). */
export const dealHeroSpot = (state: TournamentSessionState): HeroSpot => {
  const structure = payoutStructureOf(state.presetId);
  const schedule = SCHEDULE_FOR[state.presetId];
  const field = state.field;
  const handNo = field.handNo;

  // deterministic hero cards for this hand
  const dealer = createDealer(createRng(`${state.seed}::deal::${handNo}`));
  const cards = dealer.deal(2) as [CardIndex, CardIndex];
  const heroPosition = OPEN_POSITIONS[handNo % OPEN_POSITIONS.length]!;

  const sc = heroStackContext(field, schedule);
  const level = currentLevel(field, schedule);
  const icm = priceHeroField(field, structure.payoutFractions, level);
  const context = buildContext(state, structure, schedule);
  const { handClass } = classifyCombo(cards, []);
  const handLabel = HAND_CLASS_LABEL[handClass] ?? handClass.replace(/_/g, ' ');
  const cardStrings: [string, string] = [cardToString(cards[0]), cardToString(cards[1])];

  const isPushFold = isPushFoldDepth(sc.effectiveStackBB);

  if (!isPushFold) {
    return {
      handNo,
      heroCards: cards,
      heroCardStrings: cardStrings,
      heroPosition,
      handLabel,
      context,
      isPushFold: false,
      pf5Engaged: false,
    };
  }

  // THE PF5 LIVE WIRING: build a first-in-open PreflopContext and INJECT the DT Nash chart. The push/fold
  // path reads only hero.cards/position, facing (role+effBB), icm.pressure and pushFold.chart - the
  // cash-only fields (families/rules/villain) are unused on this path but supplied type-valid.
  const ctx: PreflopContext = {
    hero: { cards, position: heroPosition },
    facing: { role: 'open', effectiveStackBB: sc.effectiveStackBB },
    tableType: 'live_loose',
    tableSize: 9,
    families: {},
    villain: {
      archetype: archetypeOf('bully'),
      rangeShape: { valueHeavyPct: 45, bluffPct: 25, topClasses: [] },
    },
    playersInHand: Math.min(9, aliveCount(field)),
    positionClass: heroPosition === 'SB' ? 'OOP' : 'IP',
    ...(icm ? { icm: { pressure: icm.pressure, heroCovers: icm.heroCovers } } : {}),
    rules: [],
    pushFold: { chart: loadPushFoldChart(9) }, // <- ctx.pushFold injected live
  };
  const recommendation = recommendPreflop(ctx);
  // recommendation.pushFold present == the DT Nash path answered == PF5 engaged live (machine-readable).
  const pf5Engaged = recommendation.pushFold !== undefined;

  return {
    handNo,
    heroCards: cards,
    heroCardStrings: cardStrings,
    heroPosition,
    handLabel,
    context,
    isPushFold: true,
    pf5Engaged,
    recommendation,
    chartId: recommendation.pushFold?.chartId,
    band: recommendation.pushFold?.band,
  };
};

// ── Resolving hero's action + advancing the field ────────────────────────────────────────────────────

export type HeroAction = 'jam' | 'fold';

export interface ResolvedStep {
  state: TournamentSessionState;
  /** did hero's action match the DT Nash recommendation (chart adherence, PF5's grading philosophy)? */
  correct: boolean;
  /** the recommended action, for the coach line. */
  recommended: HeroAction;
  /** what happened to the chips this step, in plain English. */
  outcome: string;
  /** hero equity vs the call range when a jam was CONTESTED (else null). */
  contestedEquity: number | null;
}

/**
 * Resolve hero's jam/fold, apply the chip result, and advance the field one hand. Honest chip dynamics:
 *   - FOLD: hero bleeds his blind share this orbit (a small negative), field churns on.
 *   - JAM uncontested (seeded fold-around): hero wins the dead money (blinds + antes).
 *   - JAM contested: a villain calls; resolve by hero's REAL equity vs the authored call range - win
 *     doubles hero through the caller, lose costs hero the at-risk (busts if covered).
 * Deterministic in (seed, handNo).
 */
export const applyHeroAction = (
  state: TournamentSessionState,
  spot: HeroSpot,
  action: HeroAction,
): ResolvedStep => {
  const schedule = SCHEDULE_FOR[state.presetId];
  const field = state.field;
  const level = currentLevel(field, schedule);
  const rng = createRng(`${state.seed}::resolve::${field.handNo}`);

  const recommended: HeroAction =
    spot.recommendation?.finalIntent === 'jam' ? 'jam' : 'fold';
  const correct = action === recommended;

  const potDead = level.sb + level.bb + level.ante;
  let deltaChips = 0;
  let counterpartyIndex: number | undefined;
  let outcome = '';
  let contestedEquity: number | null = null;

  if (action === 'fold') {
    deltaChips = -Math.round(potDead / 9); // hero's blind share this orbit
    outcome = `Folded ${spot.handLabel}. Bled ${Math.abs(deltaChips).toLocaleString()} to the blinds.`;
  } else {
    // does someone wake up behind the jam? seeded ~40% contested at a full table, scaled by players.
    const nBehind = Math.min(8, aliveCount(field) - 1);
    const contested = rng.next() < 1 - Math.pow(0.9, Math.max(1, nBehind)); // more players -> more likely a call
    if (!contested) {
      deltaChips = potDead; // win the dead money uncontested
      outcome = `Jammed ${spot.handLabel} - folds around. Won ${potDead.toLocaleString()} in blinds + antes.`;
    } else {
      // pick the caller = biggest rival at the table (the cover threat), resolve by real equity.
      let cpIdx = -1;
      let cpChips = -1;
      for (let i = 0; i < field.stacks.length; i++) {
        if (i === 0) continue;
        if (field.stacks[i]! > cpChips) {
          cpChips = field.stacks[i]!;
          cpIdx = i;
        }
      }
      counterpartyIndex = cpIdx;
      const equity = equityProvider.handVsRange(spot.heroCards, parseRange(VILLAIN_CALL_RANGE), []).equity;
      contestedEquity = equity;
      const atRisk = Math.min(heroChips(field), cpChips);
      const won = rng.next() < equity;
      deltaChips = won ? atRisk : -atRisk;
      outcome = won
        ? `Jammed ${spot.handLabel}, called, and won at ${Math.round(equity * 100)}% - doubled through the cover.`
        : `Jammed ${spot.handLabel}, called, and lost at ${Math.round(equity * 100)}%.`;
    }
  }

  const adv = advanceField(field, schedule, { deltaChips, counterpartyIndex });
  let result: TournamentSessionState['result'] = 'running';
  let finished = false;
  if (adv.heroBusted) {
    result = 'busted';
    finished = true;
  } else if (aliveCount(adv.state) <= 1) {
    result = 'won';
    finished = true;
  }

  const bustTicker = adv.bustedThisStep > 0 ? ` ${adv.bustedThisStep} busted from the field.` : '';
  const next: TournamentSessionState = {
    ...state,
    field: adv.state,
    finished,
    result,
    lastEvent: outcome + bustTicker,
  };

  return { state: next, correct, recommended, outcome: outcome + bustTicker, contestedEquity };
};
